// Header component
import { get } from 'lodash'
import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import ShowForSizes from 'reusableComponents/ShowForSizes'
import HamburgerMenu from 'reusableComponents/HamburgerMenu'
import DropDownForHeader from 'reusableComponents/DropDownForHeader'
import { LanguageSelector } from 'components/LanguageSelector'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectIsUserVerified,
  selectShippingInfo,
  selectIsGuest,
} from 'store/shipping/shippingSelectors'
import useCookie from 'hooks/useCookie'
import PurolatorLogo from 'reusableComponents/PurolatorLogo'
import { endSession, setHasSession } from 'store/shipping/shippingSlice'
import { useLocation, useNavigate } from 'react-router-dom'
import useDetectCansAndLocker from 'hooks/useDetectCansAndLocker'
import { ReactComponent as LogOutIcon } from 'assets/svg/arrow-right-from-bracket.svg'
import { colors } from 'globalStyles'
import { logoutUser } from 'api/accounts'
import { ReactComponent as RegisterIcon } from 'assets/svg/register.svg'
import { ReactComponent as RegisterBlueIcon } from 'assets/svg/register_blue.svg'
import NavigationMenuBar from './NavigationMenuBar'
import {
  CenterContainer,
  HeaderContainer,
  LanguageSelectorWrapper,
  RightContainer,
} from './Header.style'

const getNavItemsForCansAndLocker = (t, lang) => [
  {
    menuHeading: t('header.support'),
    menuItems: [
      {
        name: t('header.contact_us'),
        href: `/cansAndLocker/dk/${lang}/support`,
      },
    ],
  },
]

const checkNavState = (disableNavigation, isAuthenticated) => {
  let navState = 0
  if (disableNavigation && isAuthenticated) navState = 1
  else if (!disableNavigation && !isAuthenticated) navState = 2
  return navState
}

const Header = ({ disableNavigation, isAuthenticated, showRedDot }) => {
  const { t, i18n } = useTranslation()
  const shippingInfo = useSelector(selectShippingInfo)
  const isGuest = useSelector(selectIsGuest)
  const navigate = useNavigate()
  const hashedPin = get(shippingInfo, 'hashedPin', '')

  const [shippingPinCookie, updateShippingPinCookie] = useCookie('hashPin', hashedPin)
  const dispatch = useDispatch()
  const userVerified = useSelector(selectIsUserVerified)
  const location = useLocation()

  const getDropDownItemsforRecipient = (lang) => [
    {
      label: t('header.my_dashboard'),
      onItemClick: () => {
        window.location.href = `/rs/tr/${lang}/recipient`
      },
    },
    {
      label: t('header.support'),
      onItemClick: () => navigate(`/rs/tr/${lang}/support`),
    },
    {
      label: t('header.logout'),
      onItemClick: () => {
        logoutUser()
        dispatch(setHasSession(false))
        dispatch(endSession())
        navigate(`/rs/tr/${lang}/login`, {
          replace: true,
        })
      },
      icon: LogOutIcon,
      iconStyle: { width: '18.28px', height: '16px', float: 'right', marginTop: '10px' },
      color: `${colors.primary_black}`,
    },
  ]

  const getDropDownItemsforGuest = (shippingPin, lang) => [
    {
      label: t('header.track'),
      onItemClick: () => navigate(`/rs/tr/${lang}/${shippingPin}`),
    },
    {
      label: t('header.support'),
      onItemClick: () => navigate(`/rs/tr/${lang}/support`),
    },
    {
      label: t('header.register_now'),
      onItemClick: () => {
        navigate(`/rs/tr/${lang}/register`, {
          replace: true,
        })
      },
      icon: RegisterIcon,
      hoveredIcon: RegisterBlueIcon,
      iconStyle: {
        width: '26px',
        height: '26px',
        float: 'left',
        marginTop: '2px',
        marginRight: '8px',
      },
    },
  ]

  // here logout option is added to end the sesion of verified users
  const getDropDownItemsforGuestWithLogOut = (shippingPin, lang) => [
    ...getDropDownItemsforGuest(shippingPin, lang),
    {
      label: t('header.logout'),
      onItemClick: () => {
        dispatch(endSession())
        window.location.replace(`/rs/tr/${lang}/${shippingPin}`)
      },
      icon: LogOutIcon,
      iconStyle: { width: '18.28px', height: '16px', float: 'right', marginTop: '10px' },
      color: `${colors.primary_black}`,
    },
  ]

  const onClickLogout = () => {
    dispatch(endSession())
  }
  const onClickPurolatorLogo = (event) => {
    if (!isGuest && isGuest !== undefined) {
      event.preventDefault()
      navigate(`/rs/tr/${i18n.language}/login`)
    }
  }
  const isCansAndLocker = useDetectCansAndLocker(location)

  useEffect(() => {
    if (hashedPin) {
      const SHIPPING_PIN_COOKIE_VALID_TIME = 2 * 24 * 60
      updateShippingPinCookie(hashedPin, SHIPPING_PIN_COOKIE_VALID_TIME)
    }
  }, [hashedPin, updateShippingPinCookie])

  return (
    <HeaderContainer>
      <PurolatorLogo lang={i18n.language} onClick={onClickPurolatorLogo}>
        Purolator, Homepage
      </PurolatorLogo>
      {!disableNavigation && (
        <CenterContainer>
          <ShowForSizes hideOnlyFor={['xs']}>
            {isCansAndLocker && (
              <NavigationMenuBar navItems={getNavItemsForCansAndLocker(t, i18n.language)} />
            )}
          </ShowForSizes>
        </CenterContainer>
      )}
      <RightContainer>
        <LanguageSelectorWrapper>
          <LanguageSelector />
        </LanguageSelectorWrapper>
        {checkNavState(disableNavigation, isAuthenticated) === 1 && (
          <DropDownForHeader dropdownItems={getDropDownItemsforRecipient(i18n.language)} />
        )}
        {checkNavState(disableNavigation, isAuthenticated) === 2 && (
          <>
            <ShowForSizes showOnlyFor={['xs']}>
              {isCansAndLocker && (
                <HamburgerMenu
                  navItems={getNavItemsForCansAndLocker(t, i18n.language)}
                  isUserVerified={userVerified}
                  logoutLabel={t('header.logout')}
                  onLogout={onClickLogout}
                />
              )}
            </ShowForSizes>
            {!isCansAndLocker && (
              <DropDownForHeader
                dropdownItems={
                  userVerified
                    ? getDropDownItemsforGuestWithLogOut(shippingPinCookie, i18n.language)
                    : getDropDownItemsforGuest(shippingPinCookie, i18n.language)
                }
                showRedDot={showRedDot}
              />
            )}
          </>
        )}
      </RightContainer>
    </HeaderContainer>
  )
}

Header.propTypes = {
  disableNavigation: PropTypes.bool,
  isAuthenticated: PropTypes.bool,
  showRedDot: PropTypes.bool,
}

Header.defaultProps = {
  disableNavigation: false,
  isAuthenticated: false,
  showRedDot: false,
}

export default Header
